import React, { useMemo, useState } from 'react';
import { useApp } from '@/contexts/AppContext';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ProviderBadge, FrameworkBadge } from '@/components/apex/Badges';
import {
  Search, ShieldAlert, ShieldCheck, Layers, FlaskConical, Building2,
  TrendingUp, AlertTriangle, ArrowRight, Database
} from 'lucide-react';
import { Input } from '@/components/ui/input';

const ENV_META = {
  production: {
    label: 'Production',
    icon: Building2,
    bar: 'bg-apex-lens',
    softBg: 'bg-apex-lens/5',
    border: 'border-apex-lens/20',
    text: 'text-apex-lens',
    pillBg: 'bg-apex-lens/10',
    description: 'Customer-facing workloads under regulatory scope.',
  },
  staging: {
    label: 'Staging',
    icon: Layers,
    bar: 'bg-amber-500',
    softBg: 'bg-amber-500/5',
    border: 'border-amber-500/20',
    text: 'text-amber-700',
    pillBg: 'bg-amber-500/10',
    description: 'Pre-production rehearsal & UAT environments.',
  },
  development: {
    label: 'Development',
    icon: FlaskConical,
    bar: 'bg-slate-400',
    softBg: 'bg-slate-100',
    border: 'border-slate-300',
    text: 'text-slate-600',
    pillBg: 'bg-slate-200',
    description: 'Sandboxes, research, and experimentation.',
  },
};

const RISK_RANK = { critical: 0, high: 1, medium: 2, low: 3 };
const RISK_TONE = {
  critical: { dot: 'bg-apex-critical', text: 'text-apex-critical', glow: 'shadow-[0_0_0_3px_rgba(220,38,38,0.10)]' },
  high:     { dot: 'bg-apex-warning',  text: 'text-apex-warning',  glow: 'shadow-[0_0_0_3px_rgba(217,119,6,0.10)]' },
  medium:   { dot: 'bg-sky-500',        text: 'text-sky-600',       glow: '' },
  low:      { dot: 'bg-emerald-500',    text: 'text-emerald-600',   glow: '' },
};

function RiskDot({ severity }) {
  const t = RISK_TONE[severity] ?? RISK_TONE.medium;
  return (
    <span className={`inline-flex items-center gap-1.5`}>
      <span className={`w-1.5 h-1.5 rounded-full ${t.dot}`} />
      <span className={`text-[10px] uppercase tracking-wider font-semibold ${t.text}`}>{severity}</span>
    </span>
  );
}

function CoverageBar({ value }) {
  const tone = value >= 95 ? '#10B981' : value >= 80 ? '#F59E0B' : '#DC2626';
  return (
    <div className="space-y-1">
      <div className="flex items-baseline justify-between">
        <span className="text-[9px] uppercase tracking-wider text-apex-neutral font-mono">Coverage</span>
        <span className="text-[11px] font-semibold tabular" style={{ color: tone }}>{value}%</span>
      </div>
      <div className="h-1 w-full rounded-full bg-slate-100 overflow-hidden">
        <motion.div
          className="h-full rounded-full"
          style={{ background: tone }}
          initial={{ width: 0 }}
          animate={{ width: `${value}%` }}
          transition={{ duration: 1.0, ease: [0.16, 1, 0.3, 1] }}
        />
      </div>
    </div>
  );
}

function AccountCard({ a, i }) {
  const env = ENV_META[a.env] ?? ENV_META.development;
  const risk = RISK_TONE[a.risk] ?? RISK_TONE.medium;
  const isIncident = a.id === 'stratum-payments-prod';
  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: i * 0.025 }}
    >
      <Link
        to={`/lens/accounts/${a.id}`}
        data-testid={`account-${a.id}`}
        className={`group relative block bg-white rounded-xl border ${isIncident ? 'border-apex-critical/40 ring-1 ring-apex-critical/10' : 'border-slate-200'} overflow-hidden hover:shadow-md hover:-translate-y-0.5 transition-all`}
      >
        {/* Environment accent bar */}
        <div className={`h-0.5 ${env.bar}`} />

        {/* Header row */}
        <div className="px-4 pt-3.5 pb-2 flex items-start justify-between gap-2">
          <div className="min-w-0">
            <h3 className="text-[13px] font-mono font-semibold text-apex-ink truncate" title={a.id}>
              {a.id}
            </h3>
            <div className="mt-0.5 flex items-center gap-2 text-[10px] text-apex-neutral">
              <span className="inline-flex items-center gap-1 font-mono uppercase tracking-wider">
                <env.icon className="w-2.5 h-2.5" />
                {env.label}
              </span>
              {a.scope.length > 0 && (
                <>
                  <span className="text-slate-300">·</span>
                  <div className="flex gap-1 flex-wrap">
                    {a.scope.map((s) => <FrameworkBadge key={s} framework={s} />)}
                  </div>
                </>
              )}
            </div>
          </div>
          <div className="flex flex-col items-end gap-1 shrink-0">
            <ProviderBadge provider="AWS" />
            <RiskDot severity={a.risk} />
          </div>
        </div>

        {/* Spotlight bar for incident account */}
        {isIncident && (
          <div className="mx-4 mb-2 px-2 py-1 rounded-md bg-apex-critical/8 flex items-center gap-1.5">
            <AlertTriangle className="w-3 h-3 text-apex-critical" />
            <span className="text-[10px] font-semibold text-apex-critical">Linked to INV-2026-042 · PCI scope</span>
          </div>
        )}

        {/* Stats footer */}
        <div className="px-4 pb-3.5 pt-1 grid grid-cols-2 gap-3 items-end">
          <CoverageBar value={a.coverage} />
          <div className="text-right">
            <div className="text-[9px] uppercase tracking-wider text-apex-neutral font-mono">Findings</div>
            <div className="flex items-baseline justify-end gap-1.5">
              <span className={`text-lg font-bold tabular ${a.findings === 0 ? 'text-apex-success' : a.findings > 3 ? 'text-apex-critical' : 'text-apex-warning'}`}>
                {a.findings}
              </span>
              <span className="text-[10px] text-apex-neutral">open</span>
            </div>
          </div>
        </div>

        {/* Hover affordance */}
        <div className="absolute right-3 bottom-3 opacity-0 group-hover:opacity-100 transition-opacity">
          <ArrowRight className="w-3.5 h-3.5 text-apex-lens" />
        </div>
      </Link>
    </motion.div>
  );
}

function PortfolioStat({ label, value, sub, tone = 'default', icon: Icon }) {
  const colorMap = {
    default: 'text-apex-ink',
    critical: 'text-apex-critical',
    warning: 'text-apex-warning',
    success: 'text-apex-success',
    lens: 'text-apex-lens',
  };
  return (
    <div className="px-4 py-3 first:pl-0 border-l border-slate-200 first:border-l-0 flex-1 min-w-0">
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-apex-neutral font-mono">
        {Icon && <Icon className="w-3 h-3" />}
        {label}
      </div>
      <div className={`mt-1 text-2xl font-bold tabular tracking-tight ${colorMap[tone]}`}>{value}</div>
      {sub && <div className="text-[11px] text-apex-neutral mt-0.5 truncate">{sub}</div>}
    </div>
  );
}

function EnvSection({ env, accounts }) {
  const meta = ENV_META[env];
  if (!accounts.length) return null;
  const Icon = meta.icon;
  const critCount = accounts.filter((a) => a.risk === 'critical').length;
  const highCount = accounts.filter((a) => a.risk === 'high').length;
  const avgCoverage = Math.round(accounts.reduce((s, a) => s + a.coverage, 0) / accounts.length);
  const totalFindings = accounts.reduce((s, a) => s + a.findings, 0);

  return (
    <section className={`rounded-2xl border ${meta.border} ${meta.softBg} overflow-hidden`} data-testid={`env-section-${env}`}>
      {/* Section header */}
      <header className="px-5 py-4 flex items-center justify-between gap-4 bg-white/40 backdrop-blur-sm border-b border-slate-200/60">
        <div className="flex items-center gap-3">
          <div className={`w-9 h-9 rounded-lg ${meta.pillBg} ${meta.text} flex items-center justify-center`}>
            <Icon className="w-4 h-4" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-base font-bold tracking-tight text-apex-ink">{meta.label}</h2>
              <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-white border border-slate-200 text-apex-neutral">
                {accounts.length} {accounts.length === 1 ? 'account' : 'accounts'}
              </span>
            </div>
            <p className="text-[11px] text-apex-neutral mt-0.5">{meta.description}</p>
          </div>
        </div>
        <div className="hidden md:flex items-center gap-4 text-[11px]">
          <div className="flex items-center gap-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-apex-critical" />
            <span className="text-apex-neutral">Critical</span>
            <span className="font-mono font-semibold text-apex-ink">{critCount}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-apex-warning" />
            <span className="text-apex-neutral">High</span>
            <span className="font-mono font-semibold text-apex-ink">{highCount}</span>
          </div>
          <div className="flex items-center gap-1.5 text-apex-neutral">
            <span>Coverage</span>
            <span className={`font-mono font-semibold ${avgCoverage >= 95 ? 'text-apex-success' : avgCoverage >= 80 ? 'text-apex-warning' : 'text-apex-critical'}`}>{avgCoverage}%</span>
          </div>
          <div className="flex items-center gap-1.5 text-apex-neutral">
            <span>Findings</span>
            <span className="font-mono font-semibold text-apex-ink">{totalFindings}</span>
          </div>
        </div>
      </header>

      {/* Cards grid */}
      <div className="p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {accounts.map((a, i) => <AccountCard key={a.id} a={a} i={i} />)}
      </div>
    </section>
  );
}

const ENV_TABS = [
  { id: 'all', label: 'All accounts' },
  { id: 'production', label: 'Production' },
  { id: 'staging', label: 'Staging' },
  { id: 'development', label: 'Development' },
];

export default function AccountLandscape() {
  const { seed } = useApp();
  const [q, setQ] = useState('');
  const [tab, setTab] = useState('all');
  const [riskFilter, setRiskFilter] = useState('all');

  const all = seed?.accounts_landscape ?? [];

  const filtered = useMemo(() => {
    return all.filter((a) => {
      if (tab !== 'all' && a.env !== tab) return false;
      if (riskFilter !== 'all' && a.risk !== riskFilter) return false;
      if (q && !a.id.toLowerCase().includes(q.toLowerCase())) return false;
      return true;
    });
  }, [all, tab, riskFilter, q]);

  const byEnv = useMemo(() => {
    const sorter = (x, y) => RISK_RANK[x.risk] - RISK_RANK[y.risk];
    return {
      production: filtered.filter((a) => a.env === 'production').sort(sorter),
      staging: filtered.filter((a) => a.env === 'staging').sort(sorter),
      development: filtered.filter((a) => a.env === 'development').sort(sorter),
    };
  }, [filtered]);

  const counts = useMemo(() => ({
    all: all.length,
    production: all.filter((a) => a.env === 'production').length,
    staging: all.filter((a) => a.env === 'staging').length,
    development: all.filter((a) => a.env === 'development').length,
  }), [all]);

  if (!seed) return null;

  // Portfolio KPIs computed from full set (not filtered)
  const prodCritical = all.filter((a) => a.env === 'production' && a.risk === 'critical').length;
  const avgCoverage = Math.round(all.reduce((s, a) => s + a.coverage, 0) / Math.max(1, all.length));
  const openFindings = all.reduce((s, a) => s + a.findings, 0);
  const inScopeAccts = all.filter((a) => a.scope.length > 0).length;

  return (
    <div className="space-y-5" data-testid="accounts">
      {/* Page header */}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <span className="text-[10px] uppercase tracking-[0.18em] text-apex-lens font-semibold">Forensics · Account Landscape</span>
          <h1 className="text-2xl font-bold tracking-tight mt-1">{seed.org.accounts_total} accounts under continuous evidence</h1>
          <p className="text-sm text-apex-neutral mt-1">
            <span className="text-apex-ink font-medium">{seed.org.accounts_production}</span> production
            <span className="mx-1.5 text-slate-300">·</span>
            <span className="text-apex-ink font-medium">{seed.org.accounts_staging}</span> staging
            <span className="mx-1.5 text-slate-300">·</span>
            <span className="text-apex-ink font-medium">{seed.org.accounts_dev}</span> development
            <span className="mx-1.5 text-slate-300">·</span>
            AWS primary, Azure secondary
          </p>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-wider text-apex-neutral font-mono">Last refresh</div>
          <div className="text-[12px] tabular text-apex-ink mt-0.5">May 7, 2026 · 18:00 UTC</div>
        </div>
      </div>

      {/* Portfolio KPI strip */}
      <div className="bg-white rounded-2xl border border-slate-200 px-4 py-1 flex flex-wrap items-stretch divide-slate-200">
        <PortfolioStat
          label="Production at critical risk"
          value={prodCritical}
          sub={prodCritical > 0 ? 'Includes stratum-payments-prod' : 'No critical findings'}
          tone="critical"
          icon={ShieldAlert}
        />
        <PortfolioStat
          label="Average coverage"
          value={`${avgCoverage}%`}
          sub="Continuous evidence collection"
          tone={avgCoverage >= 95 ? 'success' : avgCoverage >= 85 ? 'warning' : 'critical'}
          icon={ShieldCheck}
        />
        <PortfolioStat
          label="Open findings"
          value={openFindings}
          sub="Across portfolio"
          tone={openFindings > 15 ? 'warning' : 'default'}
          icon={TrendingUp}
        />
        <PortfolioStat
          label="In regulatory scope"
          value={inScopeAccts}
          sub="SOC 2 · PCI · DORA · PII"
          tone="lens"
          icon={Database}
        />
      </div>

      {/* Filters bar */}
      <div className="bg-white rounded-xl border border-slate-200 p-3 flex flex-col md:flex-row md:items-center gap-3">
        <div className="relative flex-1 min-w-0">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search accounts by id…"
            className="pl-9 h-9"
            data-testid="accounts-search"
          />
        </div>

        {/* Env tabs */}
        <div className="flex items-center gap-1 p-0.5 rounded-md bg-slate-100" data-testid="env-tabs">
          {ENV_TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`env-tab-${t.id}`}
              className={`px-2.5 h-7 rounded-md text-[12px] font-medium transition-colors flex items-center gap-1.5
                ${tab === t.id ? 'bg-white text-apex-ink shadow-sm' : 'text-apex-neutral hover:text-apex-ink'}`}
            >
              {t.label}
              <span className={`text-[10px] font-mono px-1 rounded ${tab === t.id ? 'bg-slate-100 text-apex-neutral' : 'bg-slate-200 text-apex-neutral'}`}>
                {counts[t.id]}
              </span>
            </button>
          ))}
        </div>

        {/* Risk filter */}
        <div className="flex items-center gap-1 p-0.5 rounded-md bg-slate-100" data-testid="risk-filter">
          {['all', 'critical', 'high', 'medium', 'low'].map((r) => (
            <button
              key={r}
              onClick={() => setRiskFilter(r)}
              data-testid={`risk-filter-${r}`}
              className={`px-2 h-7 rounded-md text-[11px] font-medium uppercase tracking-wider transition-colors flex items-center gap-1
                ${riskFilter === r ? 'bg-white text-apex-ink shadow-sm' : 'text-apex-neutral hover:text-apex-ink'}`}
            >
              {r !== 'all' && (
                <span className={`w-1.5 h-1.5 rounded-full ${RISK_TONE[r]?.dot ?? 'bg-slate-400'}`} />
              )}
              {r}
            </button>
          ))}
        </div>
      </div>

      {/* Empty state */}
      {filtered.length === 0 && (
        <div className="bg-white rounded-2xl border border-slate-200 py-16 text-center">
          <div className="text-apex-neutral text-sm">No accounts match the current filters.</div>
          <button
            onClick={() => { setQ(''); setTab('all'); setRiskFilter('all'); }}
            className="mt-2 text-[12px] text-apex-lens hover:underline"
            data-testid="clear-filters"
          >
            Clear filters
          </button>
        </div>
      )}

      {/* Sections by env (always grouped — even on All tab) */}
      <div className="space-y-5">
        <EnvSection env="production" accounts={byEnv.production} />
        <EnvSection env="staging" accounts={byEnv.staging} />
        <EnvSection env="development" accounts={byEnv.development} />
      </div>
    </div>
  );
}
